/**
 * Overlay Manager
 *
 * Creates and manages SVG overlay elements on top of DirectCanvas.
 * Handles connection preview and resize preview overlays.
 * Updates on ViewState change AND camera change.
 */

import type { DirectCanvas } from "@rool/renderer";
import type { ViewState, EditorNode, GuideSnapResult } from "./types";

// =============================================================================
// Types
// =============================================================================

export interface OverlayManagerContext {
  container: HTMLElement;
  canvas: DirectCanvas;
  getNodes: () => EditorNode[];
  getNodeRotation?: (nodeId: string) => number;
}

export interface OverlayManager {
  /** Update overlays based on current ViewState */
  update(state: ViewState): void;

  /** Force re-render overlays (call on camera change) */
  refresh(state: ViewState): void;

  /** Update alignment guide overlays */
  updateGuides(guides: GuideSnapResult | null): void;

  /** Clean up DOM elements */
  destroy(): void;
}

// =============================================================================
// Implementation
// =============================================================================

export function createOverlayManager(
  ctx: OverlayManagerContext,
): OverlayManager {
  const { container, canvas, getNodes } = ctx;

  // =========================================================================
  // Create SVG overlay element
  // =========================================================================

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.style.cssText = `
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		z-index: 1000;
	`;
  container.appendChild(svg);

  // =========================================================================
  // Connection overlay elements
  // =========================================================================

  const sourceHighlight = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "rect",
  );
  sourceHighlight.setAttribute("fill", "none");
  sourceHighlight.setAttribute("stroke", "#4a90d9");
  sourceHighlight.setAttribute("stroke-width", "3");
  sourceHighlight.style.display = "none";
  svg.appendChild(sourceHighlight);

  const targetHighlight = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "rect",
  );
  targetHighlight.setAttribute("fill", "none");
  targetHighlight.setAttribute("stroke", "#22c55e");
  targetHighlight.setAttribute("stroke-width", "3");
  targetHighlight.style.display = "none";
  svg.appendChild(targetHighlight);

  const previewLine = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "line",
  );
  previewLine.setAttribute("stroke", "#4a90d9");
  previewLine.setAttribute("stroke-width", "2");
  previewLine.setAttribute("stroke-dasharray", "5,5");
  previewLine.style.display = "none";
  svg.appendChild(previewLine);

  // =========================================================================
  // Resize preview element (shown during active resize only)
  // =========================================================================

  const resizePreview = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "rect",
  );
  resizePreview.setAttribute("fill", "none");
  resizePreview.setAttribute("stroke", "#4a90d9");
  resizePreview.setAttribute("stroke-width", "2");
  resizePreview.setAttribute("stroke-dasharray", "4,4");
  resizePreview.style.display = "none";
  svg.appendChild(resizePreview);

  // =========================================================================
  // Drop zone overlay (shown when files are dragged over the canvas)
  // =========================================================================

  const dropZone = document.createElement("div");
  dropZone.style.cssText = `
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		pointer-events: none;
		z-index: 999;
		display: none;
		background: rgba(74, 144, 217, 0.08);
		border: 2px dashed rgba(74, 144, 217, 0.5);
		border-radius: 8px;
		box-sizing: border-box;
	`;

  const dropZoneLabel = document.createElement("div");
  dropZoneLabel.style.cssText = `
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		color: rgba(74, 144, 217, 0.8);
		font-family: system-ui, -apple-system, sans-serif;
		font-size: 16px;
		font-weight: 500;
		user-select: none;
	`;
  dropZoneLabel.textContent = "Drop files here";
  dropZone.appendChild(dropZoneLabel);
  container.appendChild(dropZone);

  function renderDropZone(state: ViewState): void {
    dropZone.style.display = state.dropTarget ? "" : "none";
  }

  // =========================================================================
  // Edge shadow tracking for resize
  // =========================================================================

  let currentResizeShadowNodeId: string | null = null;

  function clearResizeShadow(): void {
    if (currentResizeShadowNodeId) {
      const element = canvas.getNodeElement(currentResizeShadowNodeId);
      if (element) {
        element.classList.remove(
          "resize-shadow-n",
          "resize-shadow-s",
          "resize-shadow-e",
          "resize-shadow-w",
        );
      }
      currentResizeShadowNodeId = null;
    }
  }

  // =========================================================================
  // Alignment guide overlay elements (pooled for performance)
  // =========================================================================

  const GUIDE_LINE_POOL_SIZE = 12;
  const GUIDE_TEXT_POOL_SIZE = 6;

  const guideLines: SVGLineElement[] = [];
  for (let i = 0; i < GUIDE_LINE_POOL_SIZE; i++) {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("stroke-width", "1");
    line.setAttribute("stroke-dasharray", "4,3");
    line.style.display = "none";
    svg.appendChild(line);
    guideLines.push(line);
  }

  const guideTexts: SVGTextElement[] = [];
  for (let i = 0; i < GUIDE_TEXT_POOL_SIZE; i++) {
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("font-size", "10");
    text.setAttribute("font-family", "system-ui, sans-serif");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("dominant-baseline", "central");
    text.style.display = "none";
    svg.appendChild(text);
    guideTexts.push(text);
  }

  function getGuideColor(type: string): string {
    switch (type) {
      case "center":
        return "#4A90D9";
      case "spacing":
        return "#22C55E";
      default:
        return "#FF1493";
    }
  }

  function renderGuideOverlay(guides: GuideSnapResult | null): void {
    // Hide all pooled elements first
    for (const line of guideLines) line.style.display = "none";
    for (const text of guideTexts) text.style.display = "none";

    if (!guides) return;

    const viewportWidth = container.clientWidth;
    const viewportHeight = container.clientHeight;
    let lineIdx = 0;

    // Render alignment guide lines
    for (const guide of guides.guides) {
      if (lineIdx >= guideLines.length) break;

      const line = guideLines[lineIdx++];
      const color = getGuideColor(guide.type);
      line.setAttribute("stroke", color);

      if (guide.axis === "y") {
        // Horizontal line at world y=guide.position, spanning the viewport
        const screenPt = canvas.worldToScreen(0, guide.position);
        line.setAttribute("x1", "0");
        line.setAttribute("y1", String(screenPt.y));
        line.setAttribute("x2", String(viewportWidth));
        line.setAttribute("y2", String(screenPt.y));
      } else {
        // Vertical line at world x=guide.position, spanning the viewport
        const screenPt = canvas.worldToScreen(guide.position, 0);
        line.setAttribute("x1", String(screenPt.x));
        line.setAttribute("y1", "0");
        line.setAttribute("x2", String(screenPt.x));
        line.setAttribute("y2", String(viewportHeight));
      }
      line.style.display = "";
    }

    // Render spacing guide segments + distance labels
    let textIdx = 0;
    for (const sg of guides.spacingGuides) {
      for (const seg of sg.segments) {
        if (lineIdx >= guideLines.length) break;

        const line = guideLines[lineIdx++];
        line.setAttribute("stroke", "#22C55E");
        line.setAttribute("stroke-dasharray", "2,2");

        if (sg.axis === "x") {
          // Vertical gap indicator between fromEdge and toEdge
          const from = canvas.worldToScreen(seg.fromEdge, seg.center);
          const to = canvas.worldToScreen(seg.toEdge, seg.center);
          line.setAttribute("x1", String(from.x));
          line.setAttribute("y1", String(from.y));
          line.setAttribute("x2", String(to.x));
          line.setAttribute("y2", String(to.y));
        } else {
          // Horizontal gap indicator
          const from = canvas.worldToScreen(seg.center, seg.fromEdge);
          const to = canvas.worldToScreen(seg.center, seg.toEdge);
          line.setAttribute("x1", String(from.x));
          line.setAttribute("y1", String(from.y));
          line.setAttribute("x2", String(to.x));
          line.setAttribute("y2", String(to.y));
        }
        line.style.display = "";

        // Distance label
        if (textIdx < guideTexts.length) {
          const text = guideTexts[textIdx++];
          text.setAttribute("fill", "#22C55E");
          const gapRounded = Math.round(sg.gap);

          if (sg.axis === "x") {
            const mid = canvas.worldToScreen(
              (seg.fromEdge + seg.toEdge) / 2,
              seg.center,
            );
            text.setAttribute("x", String(mid.x));
            text.setAttribute("y", String(mid.y - 8));
          } else {
            const mid = canvas.worldToScreen(
              seg.center,
              (seg.fromEdge + seg.toEdge) / 2,
            );
            text.setAttribute("x", String(mid.x + 12));
            text.setAttribute("y", String(mid.y));
          }
          text.textContent = String(gapRounded);
          text.style.display = "";
        }
      }
    }
  }

  // =========================================================================
  // Rendering helpers
  // =========================================================================

  function positionHighlightRect(rect: SVGRectElement, node: EditorNode): void {
    const topLeft = canvas.worldToScreen(node.position.x, node.position.y);
    const bottomRight = canvas.worldToScreen(
      node.position.x + node.size.width,
      node.position.y + node.size.height,
    );
    rect.setAttribute("x", String(topLeft.x));
    rect.setAttribute("y", String(topLeft.y));
    rect.setAttribute("width", String(bottomRight.x - topLeft.x));
    rect.setAttribute("height", String(bottomRight.y - topLeft.y));
  }

  function renderConnectionOverlay(state: ViewState): void {
    sourceHighlight.style.display = "none";
    targetHighlight.style.display = "none";
    previewLine.style.display = "none";

    if (!state.connecting) return;

    const nodes = getNodes();
    const sourceNode = nodes.find((n) => n.id === state.connecting!.sourceNode);
    if (!sourceNode) return;

    // Source highlight
    positionHighlightRect(sourceHighlight, sourceNode);
    sourceHighlight.style.display = "";

    // Preview line: source center → mouse
    const sourceCenter = {
      x: sourceNode.position.x + sourceNode.size.width / 2,
      y: sourceNode.position.y + sourceNode.size.height / 2,
    };
    const startScreen = canvas.worldToScreen(sourceCenter.x, sourceCenter.y);
    const endScreen = canvas.worldToScreen(
      state.connecting.currentPoint.x,
      state.connecting.currentPoint.y,
    );

    previewLine.setAttribute("x1", String(startScreen.x));
    previewLine.setAttribute("y1", String(startScreen.y));
    previewLine.setAttribute("x2", String(endScreen.x));
    previewLine.setAttribute("y2", String(endScreen.y));
    previewLine.style.display = "";

    // Target highlight
    const hoveredNodeId = state.hovered.nodeId;
    if (hoveredNodeId && hoveredNodeId !== state.connecting.sourceNode) {
      const targetNode = nodes.find((n) => n.id === hoveredNodeId);
      if (targetNode) {
        positionHighlightRect(targetHighlight, targetNode);
        targetHighlight.style.display = "";
      }
    }
  }

  function renderResizeOverlay(state: ViewState): void {
    resizePreview.style.display = "none";

    // During active resize: show preview rect
    if (state.resize) {
      const { currentBounds, rotation } = state.resize;
      const zoom = canvas.getCamera().zoom;
      const center = canvas.worldToScreen(currentBounds.cx, currentBounds.cy);
      const screenWidth = currentBounds.width * zoom;
      const screenHeight = currentBounds.height * zoom;

      resizePreview.setAttribute("x", String(center.x - screenWidth / 2));
      resizePreview.setAttribute("y", String(center.y - screenHeight / 2));
      resizePreview.setAttribute("width", String(screenWidth));
      resizePreview.setAttribute("height", String(screenHeight));

      if (rotation !== 0) {
        const rotationDeg = (rotation * 180) / Math.PI;
        resizePreview.setAttribute(
          "transform",
          `rotate(${rotationDeg}, ${center.x}, ${center.y})`,
        );
      } else {
        resizePreview.removeAttribute("transform");
      }

      resizePreview.style.display = "";

      // Apply edge shadow classes
      const { handle, nodeId } = state.resize;
      clearResizeShadow();
      const element = canvas.getNodeElement(nodeId);
      if (element) {
        currentResizeShadowNodeId = nodeId;
        if (handle.includes("n")) element.classList.add("resize-shadow-n");
        if (handle.includes("s")) element.classList.add("resize-shadow-s");
        if (handle.includes("e")) element.classList.add("resize-shadow-e");
        if (handle.includes("w")) element.classList.add("resize-shadow-w");
      }
    } else {
      clearResizeShadow();
    }
  }

  // =========================================================================
  // Public API
  // =========================================================================

  return {
    update(state: ViewState): void {
      renderConnectionOverlay(state);
      renderResizeOverlay(state);
      renderDropZone(state);
    },

    refresh(state: ViewState): void {
      renderConnectionOverlay(state);
      renderResizeOverlay(state);
      // Drop zone doesn't need refresh (it's viewport-relative, not world-relative)
    },

    updateGuides(guides: GuideSnapResult | null): void {
      renderGuideOverlay(guides);
    },

    destroy(): void {
      clearResizeShadow();
      svg.remove();
      dropZone.remove();
    },
  };
}
